import React, { FC } from 'react';

import { Link } from 'react-router-dom';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { ContentModule } from '@nhl/nhl-react-ui/lib/ContentModule';
import { useSize } from '@nhl/react-hooks/lib/useSize';

import { Record, RecordMap } from '~/types/RecordCategoriesTypes';

import TemplateKeys from '../../../../utils/contentModuleTemplateKeys';
import { FranchiseTombstone } from '../../../pages/franchise/components/franchiseTombstone';
import { twoColumnObjectFormatter } from '../utils';

import { RecordOptions, Registry } from '../types';
import { RecordContainer, RecordTableContainer, SubNavContainer } from './styles';

type Props = {
    records: RecordMap;
    page: string;
    templateKey: string;
    currentSeason: number;
    options?: RecordOptions;
};

type RecordCategoriesType = {
    pageArray: string[];
    records: RecordMap;
    registry?: Registry;
};

type RecordCategoryType = RecordCategoriesType & {
    columnCategory: string;
};

type RecordDetailType = {
    pageArray: string[];
    record: Record;
};

export const RecordCategories: FC<RecordCategoriesType> = ({
    records,
    pageArray,
    registry = { link: '', heading: '' },
}) => {
    const { width } = useSize();
    const columns = twoColumnObjectFormatter(records, width);
    return (
        <div className="records-category-content">
            {columns.map((columnCategories, index) => (
                <div className="records-column" key={index}>
                    {columnCategories.map((columnCategory) => (
                        <RecordCategory
                            key={columnCategory}
                            records={records}
                            pageArray={pageArray}
                            columnCategory={columnCategory}
                            registry={registry}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

const RecordCategory: FC<RecordCategoryType> = ({ records, pageArray, columnCategory, registry }) => {
    const selectedCategory = records[columnCategory] as Array<Record>;
    const category = selectedCategory[0].recordType.description;
    return (
        <React.Fragment>
            {registry?.link && selectedCategory[0].recordType.sequence === 1 ? <Registry registry={registry} /> : null}
            <RecordTableContainer className="records-table-container">
                <h3>{category}</h3>
                <table className="records-table striped alt">
                    <tbody>
                        {selectedCategory.map((record) =>
                            record.recordToFranchise[0].restUrl || record.recordToFranchise[0].sort ? (
                                <RecordDetail key={record.descriptionKey} pageArray={pageArray} record={record} />
                            ) : null
                        )}
                    </tbody>
                </table>
            </RecordTableContainer>
        </React.Fragment>
    );
};

const RecordDetail: FC<RecordDetailType> = ({ pageArray, record }) => (
    <tr key={record.descriptionKey}>
        <td>
            <Link to={`/${pageArray.join('/')}/${record.recordTypeSlug}/${record.descriptionKey}`}>
                {record.description}
            </Link>
        </td>
    </tr>
);

const Registry: FC<{ registry: Registry }> = ({ registry }) => (
    <RecordTableContainer className="records-table-container">
        <h3>Register</h3>
        <table className="records-table striped alt">
            <tbody>
                <tr>
                    <td>
                        <Link to={registry.link}>{registry.heading}</Link>
                    </td>
                </tr>
            </tbody>
        </table>
    </RecordTableContainer>
);

const RecordsList: FC<Props> = ({ records, page, templateKey, currentSeason, options }) => {
    const pageArray = page.slice(1).split('/');
    const firstRecord = records[Object.keys(records)[0]][0];
    const pageTitle = firstRecord.recordCategory.description;
    const seasonTitle = templateKey.startsWith('INTERNATIONAL') ? '' : `(1917-18 to ${hockeyFormatter.singleSeasonFormatter(currentSeason)})`;
    return (
        <RecordContainer>
            <ContentModule
                className="hero-module"
                baseUrl={window.recordsSettings.franchiseScopedRestUrl}
                templateKey={TemplateKeys[templateKey]}
                sequence={1}
                isPublic
            />

            {window.recordsSettings.franchiseTricode !== 'nhl' ? <FranchiseTombstone /> : null}

            {
                options?.subNav && (
                    <SubNavContainer>
                        {options?.subNav}
                    </SubNavContainer>
                )
            }

            <div className="records-content">
                <h2>{`${pageTitle} ${seasonTitle}`}</h2>
                <RecordCategories records={records} pageArray={pageArray} registry={options?.registry} />
            </div>
        </RecordContainer>
    );
};

export default RecordsList;
